import { onBeforeUnmount, onMounted, ref, type Ref } from "vue";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import type { DSTitleFieldEditorEmits } from "@/src/design_system/form_elements/title_field/types/emits";
import type { DSTitleFieldEditorProps } from "@/src/design_system/form_elements/title_field/types/internal";
import { IME_PROCESSING_KEY_CODE } from "@/src/shared/constants";

interface UseDSTitleFieldEditorOptions {
  props: Readonly<DSTitleFieldEditorProps>;
  emit: DSTitleFieldEditorEmits;
  editorRef: Ref<HTMLElement | undefined>;
  inputRef: Ref<HTMLInputElement | undefined>;
}

export const useDSTitleFieldEditor = ({ props, emit, editorRef, inputRef }: UseDSTitleFieldEditorOptions) => {
  const draft = ref(props.modelValue ?? "");
  let unmounting = false;
  onBeforeUnmount(() => {
    unmounting = true;
  });
  const { focus } = useDSInput(inputRef);
  const containsFocus = () => Boolean(editorRef.value?.contains(document.activeElement));

  function blur() {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && containsFocus()) activeElement.blur();
  }

  function handleFocusIn(event: FocusEvent) {
    if (event.relatedTarget instanceof Node && editorRef.value?.contains(event.relatedTarget)) return;
    emit("focus", event);
  }

  function cancelEditing() {
    if (props.loading) return;

    // Discard immediately even if a controlled parent keeps the editor open.
    draft.value = props.modelValue ?? "";
    emit("cancel");
  }

  function requestSave() {
    if (props.loading) return;

    // Loading disables the action buttons; keep focus on the readonly input.
    if (containsFocus()) focus();
    emit("save", draft.value);
  }

  function handleFocusOut(event: FocusEvent) {
    if (event.relatedTarget instanceof Node && editorRef.value?.contains(event.relatedTarget)) return;

    emit("blur", event);
    if (!props.loading && !unmounting) emit("save", draft.value);
  }

  function handleEnterKey(event: KeyboardEvent) {
    if (event.isComposing || event.keyCode === IME_PROCESSING_KEY_CODE || event.repeat || props.loading) return;

    event.preventDefault();
    requestSave();
  }

  function handleEscapeKey(event: KeyboardEvent) {
    event.stopPropagation();
    if (event.isComposing || props.loading) return;

    cancelEditing();
  }

  onMounted(focus);

  return {
    draft,
    focus,
    blur,
    containsFocus,
    cancelEditing,
    requestSave,
    handleFocusIn,
    handleFocusOut,
    handleEnterKey,
    handleEscapeKey,
  };
};
